import React from 'react';
import { KPI_METRICS, hasSponsoredKpis, hasOrganicKpis, normalizeKpis } from '../utils/campaignKpis';

// Stable id for React keys so rows never remount while editing.
const rid = () => Math.random().toString(36).slice(2, 9);

// Module-level so React keeps the inputs mounted across renders (otherwise the
// number field loses focus on every keystroke).
const KpiRow = ({ row, onMetric, onTarget, onRemove }) => (
  <div className="flex items-center gap-2 mb-2">
    <select
      value={row.metric}
      onChange={(e) => onMetric(e.target.value)}
      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink"
    >
      {KPI_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
    </select>
    <input
      type="number"
      value={row.target}
      onChange={(e) => onTarget(e.target.value)}
      placeholder="Target"
      className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink"
    />
    <button
      type="button"
      onClick={onRemove}
      className="text-gray-400 hover:text-red-500 text-xl leading-none px-1"
      aria-label="Remove KPI"
    >
      ×
    </button>
  </div>
);

// Editor for a campaign's KPIs, shown in the create + edit modals.
// Sections appear based on campaign `type`. Value shape = utils/campaignKpis normalizeKpis().
const CampaignKpiEditor = ({ type, value, onChange }) => {
  const kpis = normalizeKpis(value);
  const showSponsored = hasSponsoredKpis(type);
  const showOrganic = hasOrganicKpis(type);
  if (!showSponsored && !showOrganic) return null;

  const emit = (next) => onChange(next);

  // ---- sponsored list helpers ----
  const setSponsored = (list) => emit({ ...kpis, sponsored: list });
  const addSponsored = () => setSponsored([...kpis.sponsored, { _id: rid(), metric: 'impressions', target: '' }]);
  const updSponsored = (i, patch) => setSponsored(kpis.sponsored.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const rmSponsored = (i) => setSponsored(kpis.sponsored.filter((_, idx) => idx !== i));

  // ---- organic helpers ----
  const setOrganic = (patch) => emit({ ...kpis, organic: { ...kpis.organic, ...patch } });
  const addOrganic = () => setOrganic({ kpis: [...kpis.organic.kpis, { _id: rid(), metric: 'impressions', target: '' }] });
  const updOrganic = (i, patch) => setOrganic({ kpis: kpis.organic.kpis.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmOrganic = (i) => setOrganic({ kpis: kpis.organic.kpis.filter((_, idx) => idx !== i) });

  return (
    <div className="md:col-span-2 space-y-5 border-t border-gray-200 pt-4 mt-1">
      {showSponsored && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sponsored KPIs</label>
          <p className="text-xs text-gray-500 mb-2">Project totals over the whole campaign, measured from content marked <span className="font-medium">Paid</span>.</p>
          {kpis.sponsored.map((row, i) => (
            <KpiRow
              key={row._id || i}
              row={row}
              onMetric={(v) => updSponsored(i, { metric: v })}
              onTarget={(v) => updSponsored(i, { target: v })}
              onRemove={() => rmSponsored(i)}
            />
          ))}
          <button type="button" onClick={addSponsored} className="text-sm text-sc-pink hover:underline">+ Add sponsored KPI</button>
        </div>
      )}

      {showOrganic && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Organic KPIs</label>
          <p className="text-xs text-gray-500 mb-2">Measured from content <span className="font-medium">not</span> marked Paid.</p>

          {/* Total vs Monthly mode */}
          <div className="flex gap-2 mb-3">
            {[
              { v: 'total', label: 'Total (project)' },
              { v: 'monthly', label: 'Monthly (30-day)' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setOrganic({ mode: opt.v })}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  kpis.organic.mode === opt.v
                    ? 'border-sc-pink bg-sc-pink/10 text-sc-pink font-medium'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Dates by mode */}
          {kpis.organic.mode === 'monthly' ? (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Start date (each 30-day window counts from here)</label>
              <input
                type="date"
                value={kpis.organic.startDate}
                onChange={(e) => setOrganic({ startDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start date (optional)</label>
                <input
                  type="date"
                  value={kpis.organic.startDate}
                  onChange={(e) => setOrganic({ startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                <input
                  type="date"
                  value={kpis.organic.endDate}
                  onChange={(e) => setOrganic({ endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink"
                />
              </div>
            </div>
          )}

          {kpis.organic.kpis.map((row, i) => (
            <KpiRow
              key={row._id || i}
              row={row}
              onMetric={(v) => updOrganic(i, { metric: v })}
              onTarget={(v) => updOrganic(i, { target: v })}
              onRemove={() => rmOrganic(i)}
            />
          ))}
          <button type="button" onClick={addOrganic} className="text-sm text-sc-pink hover:underline">+ Add organic KPI</button>
        </div>
      )}
    </div>
  );
};

export default CampaignKpiEditor;
